import React from 'react';
import { Phone, MapPin, Mail, ArrowRight, Heart } from 'lucide-react';

const Footer = () => {
  return (
    <>
      <style>{`
        :root {
          --color-primary: #6052a5;
          --color-primary-dark: #4a3f85;
          --color-primary-light: #4B3D9A;
          --color-secondary: #e5e7eb;
          --color-text-dark: #1f2937;
          --color-text-light: #6b7280;
          --color-background-light: #f9fafb;
          --color-danger: #ef4444;
          --color-danger-light: #fee2e2;
          --color-placeholder: #95a5a6;
          --color-border: #bdc3c7;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
        }

        .enhanced-footer {
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
          color: white;
          padding: 80px 0 0;
          margin-top: auto;
          position: relative;
          overflow: hidden;
        }

        .enhanced-footer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.4;
        }

        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          position: relative;
          z-index: 1;
        }

        .footer-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1.5fr;
          gap: 50px;
          margin-bottom: 50px;
        }

        /* Logo Section */
        .footer-brand {
          animation: fadeInUp 0.6s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .brand-logo-wrapper {
          display: flex;
          align-items: center;
          margin-bottom: 25px;
          gap: 15px;
        }

        .brand-logo {
          width: 70px;
          height: 70px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
          transition: transform 0.3s ease;
          flex-shrink: 0;
        }

        .brand-logo:hover {
          transform: scale(1.05) rotate(5deg);
        }

        .brand-logo::before {
          content: 'M';
          font-size: 2rem;
          font-weight: 800;
          background: linear-gradient(135deg, var(--color-primary), var(--color-primary-dark));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .brand-text h3 {
          font-size: 2rem;
          font-weight: 700;
          margin: 0 0 5px 0;
          letter-spacing: 1px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
        }

        .brand-text p {
          font-size: 0.9rem;
          margin: 0;
          opacity: 0.95;
          font-weight: 400;
        }

        .brand-description {
          font-size: 1rem;
          line-height: 1.8;
          opacity: 0.95;
          margin: 0 0 25px 0;
        }

        .social-links {
          display: flex;
          gap: 12px;
        }

        /* Play Store Button */
        .playstore-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #000000;
          border: 1.5px solid rgba(255,255,255,0.25);
          border-radius: 10px;
          padding: 8px 16px;
          text-decoration: none;
          transition: all 0.3s ease;
          margin-top: 20px;
        }

        .playstore-btn:hover {
          background: #1a1a1a;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.35);
          text-decoration: none;
        }

        .playstore-btn-text {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
        }

        .playstore-btn-sub {
          font-size: 0.65rem;
          color: rgba(255,255,255,0.75);
          font-weight: 400;
        }

        .playstore-btn-name {
          font-size: 0.9rem;
          color: #ffffff;
          font-weight: 700;
          letter-spacing: 0.3px;
        }

        .social-link {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          cursor: pointer;
          backdrop-filter: blur(10px);
        }

        .social-link:hover {
          background: white;
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        }

        .social-link:hover svg {
          color: var(--color-primary);
        }

        /* Footer Sections */
        .footer-section {
          animation: fadeInUp 0.6s ease-out;
        }

        .footer-section:nth-child(2) {
          animation-delay: 0.1s;
        }

        .footer-section:nth-child(3) {
          animation-delay: 0.2s;
        }

        .footer-section:nth-child(4) {
          animation-delay: 0.3s;
        }

        .footer-list-title {
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 25px;
          position: relative;
          padding-bottom: 12px;
        }

        .footer-list-title::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 50px;
          height: 3px;
          background: white;
          border-radius: 2px;
        }

        /* Links */
        .footer-links-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .footer-links-list li {
          margin-bottom: 14px;
        }

        .footer-link {
          color: rgba(255, 255, 255, 0.9);
          text-decoration: none;
          font-size: 1rem;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          position: relative;
        }

        .footer-link:hover {
          color: white;
          transform: translateX(8px);
        }

        .footer-link svg {
          opacity: 0;
          transition: opacity 0.3s ease;
          width: 16px;
          height: 16px;
        }

        .footer-link:hover svg {
          opacity: 1;
        }

        /* Contact Items */
        .contact-list {
          display: flex;
          flex-direction: column;
         
        }

        .contact-item {
          display: flex;
          align-items: flex-start;
          gap: 15px;
          padding: 15px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .contact-item:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateX(5px);
        }

        .contact-icon {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .contact-icon svg {
          width: 20px;
          height: 20px;
        }

        .contact-text {
          color: rgba(255, 255, 255, 0.95);
          font-size: 0.95rem;
          line-height: 1.6;
          margin: 0;
        }

        /* Footer Bottom */
        .footer-bottom {
          border-top: 1px solid rgba(255, 255, 255, 0.15);
          padding: 30px 0;
          margin-top: 50px;
        }

        .footer-bottom-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .copyright {
          font-size: 0.95rem;
          opacity: 0.9;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .heart-icon {
          color: #ef4444;
          animation: heartbeat 1.5s ease-in-out infinite;
        }

        @keyframes heartbeat {
          0%, 100% {
            transform: scale(1);
          }
          25% {
            transform: scale(1.1);
          }
          50% {
            transform: scale(1);
          }
        }

        .footer-bottom-links {
          display: flex;
          gap: 25px;
        }

        .footer-bottom-link {
          color: rgba(255, 255, 255, 0.9);
          text-decoration: none;
          font-size: 0.95rem;
          transition: color 0.3s ease;
        }

        .footer-bottom-link:hover {
          color: white;
        }

        /* Tablet Styles */
        @media (max-width: 992px) {
          .footer-grid {
            grid-template-columns: 1fr 1fr;
            gap: 40px;
          }

          .footer-brand {
            grid-column: 1 / -1;
          }
        }

        /* Mobile Styles */
        @media (max-width: 768px) {
          .enhanced-footer {
            padding: 60px 0 0;
          }

          .footer-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .footer-brand {
            text-align: center;
          }

          .brand-logo-wrapper {
            justify-content: center;
          }

          .brand-text h3 {
            font-size: 1.8rem;
          }

          .social-links {
            justify-content: center;
          }

          .footer-list-title::after {
            left: 50%;
            transform: translateX(-50%);
          }

          .footer-links-list {
            text-align: center;
          }

          .footer-link {
            justify-content: center;
          }

          .contact-list {
            align-items: stretch;
          }

          .footer-bottom-content {
            flex-direction: column;
            text-align: center;
          }

          .footer-bottom-links {
            flex-direction: column;
            gap: 15px;
          }
        }

        /* Small Mobile Styles */
        @media (max-width: 576px) {
          .enhanced-footer {
            padding: 50px 0 0;
          }

          .footer-container {
            padding: 0 15px;
          }

          .brand-logo {
            width: 60px;
            height: 60px;
          }

          .brand-logo::before {
            font-size: 1.6rem;
          }

          .brand-text h3 {
            font-size: 1.6rem;
          }

          .brand-text p {
            font-size: 0.85rem;
          }

          .brand-description {
            font-size: 0.95rem;
          }

          .footer-list-title {
            font-size: 1.2rem;
            margin-bottom: 20px;
          }

          .footer-link,
          .contact-text {
            font-size: 0.9rem;
          }

          .contact-item {
            padding: 12px;
          }

          .contact-icon {
            width: 36px;
            height: 36px;
          }

          .social-link {
            width: 36px;
            height: 36px;
          }
        }

        @media (max-width: 400px) {
          .brand-text h3 {
            font-size: 1.4rem;
          }

          .footer-list-title {
            font-size: 1.1rem;
            text-align: center;
          }

          .footer-link,
          .contact-text,
          .copyright,
          .footer-bottom-link {
            font-size: 0.85rem;
          }
        }
      `}</style>

      <footer className="enhanced-footer">
        <div className="footer-container">
          <div className="footer-grid">
            {/* Brand Section */}
            <div className="footer-brand">
              <div className="brand-logo-wrapper">
                <div className="brand-logo"></div>
                <div className="brand-text">
                  <h3>MSZILA</h3>
                  <p>Indian Commerce Marketplace</p>
                </div>
              </div>
              <p className="brand-description">
                Join thousands of Indian businesses reaching new customers every day. Get 
                discovered, grow your network, and expand your reach across the nation.
              </p>
              {/* Play Store Download */}
              <a
                href="https://play.google.com/store/apps/developer?id=ICAME+SOFTWARE+SOLUTIONS+PRIVATE+LIMITED&hl=en_IN"
                target="_blank"
                rel="noreferrer"
                className="playstore-btn"
              >
                {/* Google Play icon */}
                <svg width="24" height="24" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M48 421.3V90.7c0-17.3 19.3-27.5 33.6-17.5l384 165.3c13.1 5.6 13.1 24 0 29.6L81.6 438.8C67.3 448.8 48 438.6 48 421.3z" fill="url(#gp1)"/>
                  <path d="M48 90.7l192 165.3L48 421.3V90.7z" fill="url(#gp2)"/>
                  <path d="M240 256L48 421.3l192-165.3z" fill="url(#gp3)"/>
                  <defs>
                    <linearGradient id="gp1" x1="48" y1="256" x2="465.6" y2="256" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#00C6FF"/><stop offset="1" stopColor="#0072FF"/>
                    </linearGradient>
                    <linearGradient id="gp2" x1="48" y1="173.3" x2="240" y2="173.3" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#00E676"/><stop offset="1" stopColor="#00BFA5"/>
                    </linearGradient>
                    <linearGradient id="gp3" x1="48" y1="338.7" x2="240" y2="338.7" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FF6D00"/><stop offset="1" stopColor="#FFAB00"/>
                    </linearGradient>
                  </defs>
                </svg>
                <div className="playstore-btn-text">
                  <span className="playstore-btn-sub">GET IT ON</span>
                  <span className="playstore-btn-name">Google Play</span>
                </div>
              </a>

              <div className="social-links" style={{marginTop: '16px'}}>
                <div className="social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <div className="social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </div>
                <div className="social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                  </svg>
                </div>
                <div className="social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* Useful Links */}
            <div className="footer-section">
              <h4 className="footer-list-title">Useful Links</h4>
              <ul className="footer-links-list">
                <li>
                  <a href="/" className="footer-link">
                    Home
                    <ArrowRight />
                  </a>
                </li>
                <li>
                  <a href="/about" className="footer-link">
                    About Us
                    <ArrowRight />
                  </a>
                </li>
                <li>
                  <a href="/merchant-search" className="footer-link">
                    categories
                    <ArrowRight />
                  </a>
                </li>
                {/* <li>
                  <a href="#pricing" className="footer-link">
                    Pricing
                    <ArrowRight />
                  </a>
                </li> */}  
                <li>
                  <a href="/contact" className="footer-link">
                    Contact Us
                    <ArrowRight />
                  </a>
                </li>
              </ul>
            </div>

            {/* Helpful Links */}
            <div className="footer-section">
              <h4 className="footer-list-title">Helpful Links</h4>
              <ul className="footer-links-list">
                <li>
                  <a href="/privacy-policy" className="footer-link">
                    Privacy Policy
                    <ArrowRight />
                  </a>
                </li>
                <li>
                  <a href="/cancellation-and-refunds" className="footer-link">
                    Refund Policy
                    <ArrowRight />
                  </a>
                </li>
                <li>
                  <a href="/terms-and-conditions" className="footer-link">
                    Terms & Conditions
                    <ArrowRight />
                  </a>
                </li>
              </ul>
            </div>

            {/* Get in Touch */}
            <div className="footer-section">
              <h4 className="footer-list-title">Get in Touch</h4>
              <div className="contact-list">
                <div className="contact-item">
                  <div className="contact-icon">
                    <Phone />
                  </div>
                  <p className="contact-text">9997427354</p>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <MapPin />
                  </div>
                  <p className="contact-text">
                    NO1188, KHARPARI, Mainpuri, Uttar Pradesh - 205001
                  </p>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Mail />
                  </div>
                  <p className="contact-text">connectus@mszila.in</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="footer-bottom">
            <div className="footer-bottom-content justify-content-center">
              <p className="copyright text">
                © 2026 ICAME Software Solutions Pvt. Ltd. Made with <Heart className="heart-icon" size={16} /> in India
              </p>
              {/* <div className="footer-bottom-links">
                <a href="/sitemap" className="footer-bottom-link">Sitemap</a>
                <a href="/faq" className="footer-bottom-link">FAQ</a>
                <a href="/support" className="footer-bottom-link">Support</a>
              </div> */}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;